import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import questionnairesService from '../api/questionnairesService';
import type {
    CreateOptionDto,
    CreateQuestionDto,
    CreateQuestionnaireDto,
    UpdateOptionDto,
    UpdateQuestionDto,
    UpdateQuestionnaireDto,
} from '../types/questionnaires';

const key = ['questionnaires'] as const;

export const useQuestionnaires = () =>
    useQuery({
        queryKey: key,
        queryFn: () => questionnairesService.getAll(),
        refetchOnWindowFocus: false,
    });

export const useQuestionnaireResults = (id: number | null) =>
    useQuery({
        queryKey: ['questionnaireResults', id],
        queryFn: () => questionnairesService.getResults(id!),
        enabled: id != null,
        refetchOnWindowFocus: false,
    });

function useInvalidate() {
    const qc = useQueryClient();
    return () => qc.invalidateQueries({ queryKey: key });
}

export const useCreateQuestionnaire = () => {
    const invalidate = useInvalidate();
    return useMutation({
        mutationFn: (dto: CreateQuestionnaireDto) => questionnairesService.create(dto),
        onSuccess: invalidate,
    });
};

export const useUpdateQuestionnaire = () => {
    const invalidate = useInvalidate();
    return useMutation({
        mutationFn: ({ id, dto }: { id: number; dto: UpdateQuestionnaireDto }) =>
            questionnairesService.update(id, dto),
        onSuccess: invalidate,
    });
};

export const useDeleteQuestionnaire = () => {
    const invalidate = useInvalidate();
    return useMutation({
        mutationFn: (id: number) => questionnairesService.remove(id),
        onSuccess: invalidate,
    });
};

export const useCreateQuestion = () => {
    const invalidate = useInvalidate();
    return useMutation({
        mutationFn: ({ questionnaireId, dto }: { questionnaireId: number; dto: CreateQuestionDto }) =>
            questionnairesService.createQuestion(questionnaireId, dto),
        onSuccess: invalidate,
    });
};

export const useUpdateQuestion = () => {
    const invalidate = useInvalidate();
    return useMutation({
        mutationFn: ({ id, dto }: { id: number; dto: UpdateQuestionDto }) =>
            questionnairesService.updateQuestion(id, dto),
        onSuccess: invalidate,
    });
};

export const useDeleteQuestion = () => {
    const invalidate = useInvalidate();
    return useMutation({
        mutationFn: (id: number) => questionnairesService.removeQuestion(id),
        onSuccess: invalidate,
    });
};

export const useCreateOption = () => {
    const invalidate = useInvalidate();
    return useMutation({
        mutationFn: ({ questionId, dto }: { questionId: number; dto: CreateOptionDto }) =>
            questionnairesService.createOption(questionId, dto),
        onSuccess: invalidate,
    });
};

export const useUpdateOption = () => {
    const invalidate = useInvalidate();
    return useMutation({
        mutationFn: ({ id, dto }: { id: number; dto: UpdateOptionDto }) =>
            questionnairesService.updateOption(id, dto),
        onSuccess: invalidate,
    });
};

export const useDeleteOption = () => {
    const invalidate = useInvalidate();
    return useMutation({
        mutationFn: (id: number) => questionnairesService.removeOption(id),
        onSuccess: invalidate,
    });
};
